"use client";

import * as React from "react";

import { COUNTRY_CODES } from "@unreserved/configs";
import { DEFAULT_COUNTRY_CODE } from "@unreserved/constants";
import { Input } from "@unreserved/ui/components/input";
import { Label } from "@unreserved/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@unreserved/ui/components/select";
import { cn } from "@unreserved/utils";
import { type VariantProps, cva } from "class-variance-authority";

import { InfoWarningIcon } from "@/app/_components/icons/info-warning";

const inputVariants = cva(
  "flex w-full rounded-md border !bg-white p-3 text-sm !text-typography-950 transition-all placeholder:!text-typography-600 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ring-0 focus-visible:ring-4 shadow-none h-auto",
  {
    variants: {
      variant: {
        default:
          "border-border-400 focus-visible:border-primary-500 focus-visible:ring-primary-200",
        error: "border-error-700 focus-visible:border-error-700 focus-visible:ring-error-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: string;
  error?: string;
  variant?: VariantProps<typeof inputVariants>["variant"];
  countryCode?: string;
  onCountryCodeChange?: (code: string) => void;
  isRequired?: boolean;
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      className,
      variant,
      label,
      error,
      disabled,
      countryCode = DEFAULT_COUNTRY_CODE,
      onCountryCodeChange,
      isRequired = false,
      ...props
    },
    ref,
  ) => {
    // Determine variant based on error state
    const computedVariant = error ? "error" : variant;

    return (
      <div className="w-full">
        {label && (
          <Label
            htmlFor={props.id || props.name}
            className="text-typography-900 mb-2 block rounded-md text-sm font-medium">
            {label}
            {isRequired && <span className="text-error-500">*</span>}
          </Label>
        )}
        <div className="flex gap-2">
          <Select value={countryCode} onValueChange={onCountryCodeChange} disabled={disabled}>
            <SelectTrigger
              className={cn(
                "!h-auto rounded-md border !bg-white text-sm text-black [&_svg]:!text-[#242424] [&_svg]:!opacity-100",
                computedVariant === "error"
                  ? "border-error-600 focus-visible:border-error-700 focus-visible:ring-error-200"
                  : "border-border-400 focus-visible:border-primary-500 focus-visible:ring-primary-200",
              )}>
              <SelectValue placeholder={DEFAULT_COUNTRY_CODE} />
            </SelectTrigger>
            <SelectContent className="ml-2 bg-white">
              {COUNTRY_CODES.map((country) => (
                <SelectItem
                  key={country.code}
                  value={country.code}
                  className="data-[state=checked]:bg-primary-500 data-[state=checked]:text-white">
                  {country.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative flex-1">
            <Input
              type="tel"
              className={cn(inputVariants({ variant: computedVariant }), className)}
              ref={ref}
              disabled={disabled}
              aria-invalid={!!error}
              aria-describedby={error ? `${props.id || props.name}-error` : undefined}
              {...props}
            />
          </div>
        </div>
        {error && (
          <div className="mt-1 flex flex-row items-start gap-1">
            <div className="mt-0.5">
              <InfoWarningIcon width={13} height={13} />
            </div>
            <p
              id={`${props.id || props.name}-error`}
              className="text-error-600 text-sm"
              role="alert">
              {error}
            </p>
          </div>
        )}
      </div>
    );
  },
);

PhoneInput.displayName = "PhoneInput";
